import {buildStandardHeaders, otsBaseApi} from "../../ots-api";
import {TransportSearchCriteria} from "../../../model/search/tsc";
import {
  MapTtasCriteria, MultiTicketValidationDTO,
  TicketSaveReceipt,
  TicketValidationDTO,
  TransportAssoc,
  TransportAssocEntity,
  TtaSave
} from "../../../model/transport-assoc";
import {removeUndefined} from "../../../util/util";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import {OtsAsyncThunkConfigExtra, OtsState} from "../../ots-store";
import {AnyAction, ThunkDispatch} from "@reduxjs/toolkit";
import {Shift} from "../../../model/shift";
import {TtaMappedPair} from "../../../model/tta-mapped-pair";
import {driverActivityCacheUpdate, splitTtaInDriverActivity, upsertTtaIntoDriverActivity} from "../driver-activity/driver-activity-store";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Ttas", "SettlementTtas", "ActivityPeriodTtas", "DriverActivity"]});


/**
 * Puts the ticket a save or an association wrote into the loaded days of driver activity: the receipt names it, so it
 * is fetched alone rather than refetching the day. A save that went to holding is not listed with live activity, so
 * the day is fetched again.
 */
const mergeSavedTicket = async (
  _arg: unknown,
  {dispatch, queryFulfilled, getState}: {dispatch: ThunkDispatch<OtsState, OtsAsyncThunkConfigExtra<OtsState>, AnyAction>, queryFulfilled: Promise<{data: TicketSaveReceipt}>, getState: () => unknown},
) => {
  let receipt: TicketSaveReceipt | undefined
  try {
    receipt = (await queryFulfilled).data
  } catch {
    return
  }
  try {
    if (!receipt.ttaId || (receipt.ttaLocation ?? receipt.location) === "HOLDING") throw new Error("No live ticket to merge")
    const tta = await dispatch(ttaApi.endpoints.getTta.initiate({id: receipt.ttaId, database: undefined}, {subscribe: false, forceRefetch: true})).unwrap()
    driverActivityCacheUpdate(dispatch, getState() as Parameters<typeof driverActivityCacheUpdate>[1], (draft, window) => upsertTtaIntoDriverActivity(draft, tta, window))
  } catch (error) {
    console.debug(error)
    dispatch(ttaApi.util.invalidateTags([{type: "DriverActivity"}]))
  }
}

export const ttaApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTta: builder.query<TransportAssoc, {
      id: string,
      database: string | undefined
    }>({
      query: ({id, database}) => ({
        url: database ? `/tta/${id}?serviceLookup=${database}` : `/tta/${id}`,
        method: "GET"
      }),
      providesTags: (result) => {
        if (result) {
          return [
            {type: "Ttas", id: result.id}
          ];
        } else {
          return [];
        }
      }
    }),
    getTtaByPickupAndDelivery: builder.query<TransportAssoc, {
      pickupId: string,
      deliveryId: string,
      database: string | undefined
    }>({
      query: ({pickupId, deliveryId, database}) => ({
        url: `/tta`,
        method: "GET",
        params: removeUndefined({
          pickupTransportId: pickupId,
          deliveryTransportId: deliveryId,
          serviceLookup: database
        })
      }),
      providesTags: (result) => {
        if (result) {
          return [
            {type: "Ttas", id: result.id}
          ];
        } else {
          return [];
        }
      }
    }),
    searchTtas: builder.query<
      TransportAssoc[],
      TransportSearchCriteria
    >({
      query: ({limit, ...criteria}) => ({
        url: "/tta/search",
        method: "POST",
        body: criteria,
        params: limit != null ? {limit} : undefined
      }),
      keepUnusedDataFor: 5 * 60,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Ttas", id} as const)
            ),
            {type: "Ttas", id: "LIST"}
          ];
        } else {
          return [{type: "Ttas", id: "LIST"}];
        }
      }
    }),
    searchDispatchTtas: builder.query<
      TransportAssoc[],
      TransportSearchCriteria
    >({
      query: (criteria) => ({
        url: "/dispatch/transport/search",
        method: "POST",
        body: removeUndefined(criteria)
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Ttas", id} as const)
            ),
            {type: "Ttas", id: "LIST"}
          ];
        } else {
          return [{type: "Ttas", id: "LIST"}];
        }
      }
    }),
    getTtasBySettlement: builder.query <
      TransportAssoc[],
      { settlementId: string, criteria?: TransportSearchCriteria }
    >({
      query: (arg) => ({
        url: `/tta/settlement/${arg.settlementId}`,
        method: "POST",
        body: arg.criteria
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Ttas", id} as const)
            ),
            {type: "SettlementTtas", id: arg.settlementId}
          ];
        } else {
          return [{type: "SettlementTtas", id: arg.settlementId}];
        }
      }
    }),
    getTtasByActivityPeriod: builder.query <
      TransportAssoc[],
      { activityPeriodId: string, criteria?: TransportSearchCriteria }
    >({
      query: (arg) => ({
        url: `/tta/activity-period/${arg.activityPeriodId}`,
        method: "POST",
        body: arg.criteria
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Ttas", id} as const)
            ),
            {type: "ActivityPeriodTtas", id: arg.activityPeriodId}
          ];
        } else {
          return [{type: "ActivityPeriodTtas", id: arg.activityPeriodId}];
        }
      }
    }),
    getDispatchTtasByDay: builder.query<
      TransportAssoc[],
      {
        day: string,
        regionId: string
      }
    >({
      query: (arg) => ({
        url: `/dispatch/transport?day=${arg.day}&regionId=${arg.regionId}`,
        method: "GET",
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Ttas", id} as const)
            ),
            {type: "Ttas", id: "LIST"}
          ];
        } else {
          return [{type: "Ttas", id: "LIST"}];
        }
      }
    }),
    mapTtas: builder.query<TtaMappedPair[], MapTtasCriteria>({
      query: ({mappingFileId, companyId, file}) => {
        const formData = new FormData()
        formData.append("mappingFileId", mappingFileId)
        formData.append("file", file)
        if (companyId) {
          formData.append("companyId", companyId)
        }
        return ({
          url: `/tta/map`,
          method: 'POST',
          body: formData,
          formData: true,
        })
      },
    }),
    getTicketMappingIds: builder.query<string[], void>({
      query: () => {
        return ({
          url: `/tta/watergoticketmappingnames`,
          method: 'GET',
        })
      },
    }),
    saveTta: builder.mutation<TicketSaveReceipt, TtaSave>({
      query: (arg) => ({
        url: "/tta/save",
        method: "POST",
        body: arg
      }),
      // Days of driver activity are patched with the saved ticket instead of being fetched again; filtered
      // searches have no window to patch, so they refetch.
      invalidatesTags: (result, error, {id}) => {
        if (id) {
          return [
            {type: 'Ttas', id},
            {type: "Ttas", id: "LIST"},
            {type: "DriverActivity", id: "LIST"},
          ]
        } else {
          return [
            {type: "Ttas", id: "LIST"},
            {type: "DriverActivity", id: "LIST"},
          ]
        }
      },
      onQueryStarted: mergeSavedTicket,
    }),
    deleteTta: builder.mutation<void, Partial<TransportAssoc>>({
      query: (tta) => ({
        url: '/tta',
        method: 'DELETE',
        body: tta,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: 'Ttas', id},
        {type: "DriverActivity", id: "LIST"},
      ],
      // The pickup and delivery stay; in a day of driver activity they go back to being listed on their own.
      async onQueryStarted({id}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          if (id) driverActivityCacheUpdate(dispatch, getState(), (draft) => splitTtaInDriverActivity(draft, id))
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    deleteTtaAll: builder.mutation<void, Partial<TransportAssoc>>({
      query: (tta) => ({
        url: '/tta/all',
        method: 'DELETE',
        body: tta,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Ttas', id}],
      async onQueryStarted({id}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            const index = draft.findIndex(s => s.transportTransportAssoc?.id === id)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    /** A link to one ticket's PDF (`GET /tta/{id}/pdf`); the server answers with a temporary download URL. */
    getTicketPdfLink: builder.mutation<string, { id: string, database?: string }>({
      query: ({id, database}) => ({
        url: `/tta/${id}/pdf`,
        method: "GET",
        params: removeUndefined({database}),
        responseHandler: "text",
      }),
    }),
    /** A link to a zip of several tickets' PDFs (`POST /tta/bulk-pdf`); the server reads the tickets themselves. */
    getTicketBulkPdfLink: builder.mutation<string, TransportAssoc[]>({
      query: (ttas) => ({
        url: "/tta/bulk-pdf",
        method: "POST",
        body: ttas,
        responseHandler: "text",
      }),
    }),
    /** A link to the tickets as an Excel sheet (`POST /tta/download`), WaterGo's Ticket Excel download. */
    getTicketExcelLink: builder.mutation<string, { ttas: TransportAssoc[], primaryCompany?: boolean }>({
      query: ({ttas, primaryCompany = false}) => ({
        url: "/tta/download",
        method: "POST",
        params: {primaryCompany},
        body: ttas,
        responseHandler: "text",
      }),
    }),
    validateTta: builder.mutation<void, { id: string, database?: string, validation: TicketValidationDTO }>({
      query: ({id, database, validation}) => ({
        url: `/tickets/${id}/validate`,
        method: "PUT",
        params: removeUndefined({
          serviceLookup: database,
        }),
        body: validation
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Ttas', id}],
      async onQueryStarted({id, database}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          const response = await dispatch(ttaApi.endpoints.getTta.initiate({id: id, database: database})).unwrap()
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            const data = draft.find(s => s.transportTransportAssoc?.id === id)
            if (data?.transportTransportAssoc) {
              data.transportTransportAssoc.adminValidatedDttm = response?.adminValidatedDttm
              data.transportTransportAssoc.adminValidatedById = response?.adminValidatedById
              data.transportTransportAssoc.adminValidatorName = response?.adminValidatorName
              data.transportTransportAssoc.dispatchValidatedDttm = response?.dispatchValidatedDttm
              data.transportTransportAssoc.dispatchValidatedById = response?.dispatchValidatedById
              data.transportTransportAssoc.dispatchValidatorName = response?.dispatchValidatorName
              data.transportTransportAssoc.fieldValidatedDttm = response?.fieldValidatedDttm
              data.transportTransportAssoc.fieldValidatedById = response?.fieldValidatedById
              data.transportTransportAssoc.fieldValidatorName = response?.fieldValidatorName
              data.transportTransportAssoc.businessValidatedDttm = response?.businessValidatedDttm
              data.transportTransportAssoc.businessValidatedById = response?.businessValidatedById
              data.transportTransportAssoc.businessValidatorName = response?.businessValidatorName
              data.transportTransportAssoc.validationStatus = response?.validationStatus
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    validateTtas: builder.mutation<void, { validations: MultiTicketValidationDTO[] }>({
      query: ({validations}) => ({
        url: `/tickets/validate`,
        method: "PUT",
        body: validations
      }),
      invalidatesTags: (result, error, {validations}) => validations.map((v) => ({type: 'Ttas', id: v.ticketId})),
      async onQueryStarted({validations}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          for (const validation of validations) {
            const response = await dispatch(ttaApi.endpoints.getTta.initiate({id: validation.ticketId, database: validation.serviceLookup})).unwrap()
            driverActivityCacheUpdate(dispatch, getState(), (draft) => {
              const data = draft.find(s => s.transportTransportAssoc?.id === validation.ticketId)
              if (data?.transportTransportAssoc) {
                data.transportTransportAssoc.adminValidatedDttm = response?.adminValidatedDttm
                data.transportTransportAssoc.adminValidatedById = response?.adminValidatedById
                data.transportTransportAssoc.adminValidatorName = response?.adminValidatorName
                data.transportTransportAssoc.dispatchValidatedDttm = response?.dispatchValidatedDttm
                data.transportTransportAssoc.dispatchValidatedById = response?.dispatchValidatedById
                data.transportTransportAssoc.dispatchValidatorName = response?.dispatchValidatorName
                data.transportTransportAssoc.fieldValidatedDttm = response?.fieldValidatedDttm
                data.transportTransportAssoc.fieldValidatedById = response?.fieldValidatedById
                data.transportTransportAssoc.fieldValidatorName = response?.fieldValidatorName
                data.transportTransportAssoc.businessValidatedDttm = response?.businessValidatedDttm
                data.transportTransportAssoc.businessValidatedById = response?.businessValidatedById
                data.transportTransportAssoc.businessValidatorName = response?.businessValidatorName
                data.transportTransportAssoc.validationStatus = response?.validationStatus
              }
            })
          }
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    /** Pairs a pickup with a delivery; either may already be on another ticket, which is then split from it. */
    associateTtas: builder.mutation<TicketSaveReceipt, {pickupTransportId: string, deliveryTransportId: string}>({
      query: (body) => ({
        url: "/tta/associate",
        method: "POST",
        body,
      }),
      // The tickets the transports left are rewritten too, so filtered searches refetch.
      invalidatesTags: [{type: "Ttas", id: "LIST"}, {type: "DriverActivity", id: "LIST"}],
      onQueryStarted: mergeSavedTicket,
    }),
    /** Splits the ticket's shift in two at the ticket. The day is fetched again: both shifts' rows change. */
    splitShiftAtTta: builder.mutation<Shift[], Partial<TransportAssoc>>({
      query: (tta) => ({
        url: "/tta/splitshift",
        method: "POST",
        body: tta,
      }),
      invalidatesTags: [{type: "DriverActivity"}],
    }),
    /** Splits costed tickets' pay evenly across them. */
    splitTtasEvenly: builder.mutation<TransportAssoc[], {ttas: Partial<TransportAssoc>[], regionId: string}>({
      query: ({ttas, regionId}) => ({
        url: "/tta/splittransportevenly",
        method: "POST",
        params: {regionId},
        body: ttas,
      }),
      invalidatesTags: (result, error, {ttas}) => [
        ...ttas.map(({id}) => ({type: "Ttas", id} as const)),
        {type: "DriverActivity"},
      ],
    }),
    undoTtaSplits: builder.mutation<TransportAssoc[], Partial<TransportAssoc>[]>({
      query: (ttas) => ({
        url: '/tta/splittransports',
        method: 'POST',
        body: ttas,
      }),
      invalidatesTags: (result, error, arg) => [
        ...arg.map(({id}) => ({type: "Ttas", id} as const)),
        {type: "DriverActivity"},
      ],
    }),
    createTtaPickupSplits: builder.mutation<TransportAssoc[], Partial<TransportAssoc>[]>({
      query: (ttas) => ({
        url: '/tta/create-pickup-split',
        method: 'POST',
        body: ttas,
      }),
      invalidatesTags: (result, error, arg) => [
        ...arg.map(({id}) => ({type: "Ttas", id} as const)),
        {type: "DriverActivity"},
      ],
    }),
    createTtaDeliverySplits: builder.mutation<TransportAssoc[], Partial<TransportAssoc>[]>({
      query: (ttas) => ({
        url: '/tta/create-delivery-split',
        method: 'POST',
        body: ttas,
      }),
      invalidatesTags: (result, error, arg) => [
        ...arg.map(({id}) => ({type: "Ttas", id} as const)),
        {type: "DriverActivity"},
      ],
    }),
  }),
  overrideExisting: false
});

interface TicketsDownloadArg {
  ttas: TransportAssocEntity[],
  primaryCompany?: boolean
}

export const downloadTicketsReport = (arg: TicketsDownloadArg, api: BaseQueryApi): Promise<Response> => {
  return TransportAssocEntity.loadTypeValues(arg.ttas)
    .then((ttas) => {
      const headers = buildStandardHeaders(api)
      headers["Accept"] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      headers["Content-Type"] = 'application/json'

      const request: RequestInit = {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(ttas),
      }

      const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;
      const url = `${baseUrl}/tta/v2/download?primaryCompany=${arg.primaryCompany ?? true}`;

      return window.fetch(url, request)
        .then((response) => {
          if (response.ok) {
            return Promise.resolve(response)
          } else {
            return Promise.reject(response)
          }
        });
    })
}

export interface TtaCompareReportDownloadArg {
  ttaMappedPairs: Record<string, any>[],
  rateModelId?: string
  regionId?: string
}

export const downloadTtaCompareReport = (arg: TtaCompareReportDownloadArg, api: BaseQueryApi): Promise<Response> => {
  const headers = buildStandardHeaders(api)
  headers["Accept"] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  headers["Content-Type"] = 'application/json'

  const request: RequestInit = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(arg.ttaMappedPairs),
  }

  const params = new URLSearchParams(removeUndefined({
    rateModelId: arg.rateModelId,
    regionId: arg.regionId,
  }))

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;
  const url = `${baseUrl}/tta/download-compare-report?${params}`;

  return window.fetch(url, request)
}

export const downloadTicketPdf = (tta: TransportAssocEntity, api: BaseQueryApi): Promise<Response> => {
  const headers = buildStandardHeaders(api)
  headers["Accept"] = 'application/x-zip'
  headers["Content-Type"] = 'application/x-zip'

  const request: RequestInit = {
    method: 'GET',
    headers: headers,
  }

  const params = new URLSearchParams(removeUndefined({
    database: tta.database,
    bytes: true,
  }))

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;
  const url = `${baseUrl}/tta/${tta.id}/pdf?${params}`;

  return window.fetch(url, request)
}

const usePrefetch = ttaApi.usePrefetch;

export {usePrefetch as useTtaPrefetch};

export const {
  useGetTtaQuery,
  useGetTtaByPickupAndDeliveryQuery,
  useSearchTtasQuery,
  useGetTtasBySettlementQuery,
  useGetTtasByActivityPeriodQuery,
  useGetDispatchTtasByDayQuery,
  useSearchDispatchTtasQuery,
  useLazyMapTtasQuery,
  useGetTicketMappingIdsQuery,
  useSaveTtaMutation,
  useDeleteTtaMutation,
  useDeleteTtaAllMutation,
  useValidateTtaMutation,
  useGetTicketPdfLinkMutation,
  useGetTicketBulkPdfLinkMutation,
  useGetTicketExcelLinkMutation,
  useValidateTtasMutation,
  useUndoTtaSplitsMutation,
  useCreateTtaPickupSplitsMutation,
  useCreateTtaDeliverySplitsMutation,
  useAssociateTtasMutation,
  useSplitShiftAtTtaMutation,
  useSplitTtasEvenlyMutation,
} = ttaApi;
